import { useState } from "react"
import styled from "styled-components"
import useApi from "../../hooks/useApi"
import useLocale from "../../hooks/useLocale"
import useTranslation from "../../hooks/useTranslation"
import { FormGroup } from "../form/FormComponents"
import IconButton from "../generic/IconButton"
import { Icon_CheckMark, Icon_Download } from "../../styles/icons"

/*
 * An EU VAT ID with its VIES check (services/vies). The field bound to `id`
 * holds the VAT ID; the check's result lives in four sibling fields named
 * after it — for `vatId`: vatIdStatus ('unchecked' | 'valid' | 'invalid'),
 * vatIdCheckedAt, vatIdName, vatIdAddress.
 *
 * Editing the ID resets the result to 'unchecked': a result belongs to the ID
 * it was checked for. A VIES outage is shown and leaves the result untouched.
 * On a sales invoice the status decides reverse charge (vatTaxList,
 * validationLogicData.invoice.book).
 *
 * `valueMap` (optional) offers to overwrite the record with what VIES returned
 * for a valid ID — form field → path in the VIES answer:
 *   { name: 'name', address_street: 'addressParts.street', country: 'country' }
 * `countryName` comes with the answer, in the organisation's language. A value VIES did
 * not give (null) leaves the field as it is: a postcode VIES' free-text
 * address could not be read from is not a reason to clear the one on file.
 * After a reload the address parts are gone, so applying re-checks first.
 */

const Row = styled.div`
    display: flex;
    gap: 0.5rem;
    align-items: center;
`

const Badge = styled.span`
    display: inline-block;
    margin-top: 0.4rem;
    padding: 0.2rem 0.6rem;
    border-radius: 0.375rem;
    font-size: 0.8rem;
    background-color: ${({ $tone, theme }) => theme.emotive[$tone].bg};
    color: ${({ $tone, theme }) => theme.emotive[$tone].color};
    border: 1px solid ${({ $tone, theme }) => theme.emotive[$tone].border};
`

const Detail = styled.div`
    margin-top: 0.25rem;
    font-size: 0.85em;
    color: ${({ theme }) => theme.font.label};
`

const TONE = { valid: 'success', invalid: 'error', unchecked: 'disabled' }

const readPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj)

const VatChecker = ({ name, inputId, value, setValue, placeholder, useStore, valueMap, required, inputRef, testId }) => {
    const { t } = useTranslation()
    const { getMe } = useApi()
    const { ld } = useLocale()

    const [status] = useStore(s => s[`${name}Status`])
    const [checkedAt] = useStore(s => s[`${name}CheckedAt`])
    const [companyName] = useStore(s => s[`${name}Name`])
    const [companyAddress] = useStore(s => s[`${name}Address`])

    const [busy, setBusy] = useState(false)
    const [problem, setProblem] = useState(null)
    const [answer, setAnswer] = useState(null)       // the last VIES answer, for applying it

    const field = (suffix, v) => ({ target: { name: `${name}${suffix}`, value: v } })

    const onChange = (e) => {
        setProblem(null)
        setAnswer(null)
        setValue([
            { target: { name, value: e.target.value.toUpperCase() } },
            field('Status', 'unchecked'),
            field('CheckedAt', null),
            field('Name', null),
            field('Address', null)
        ])
    }

    // Resolves to the VIES answer, or null after showing why there is none.
    const runCheck = async () => {
        setProblem(null)
        const res = await getMe(`vies/check?vatId=${encodeURIComponent(value || '')}`)
        // getMe hands back the error response instead of throwing.
        if (!res?.status || res.data?.error) {
            setProblem(res?.data?.error || 'VIES_UNAVAILABLE')
            return null
        }
        setAnswer(res)
        setValue([
            { target: { name, value: res.vatId } },
            field('Status', res.status),
            field('CheckedAt', res.checkedAt),
            field('Name', res.name),
            field('Address', res.address)
        ])
        return res
    }

    const check = async () => {
        setBusy(true)
        await runCheck()
        setBusy(false)
    }

    const apply = async () => {
        setBusy(true)
        const res = answer ?? await runCheck()
        if (res?.status === 'valid') {
            const changes = Object.entries(valueMap)
                .map(([target, path]) => ({ target: { name: target, value: readPath(res, path) } }))
                .filter(change => change.target.value !== null && change.target.value !== undefined)
            setValue(changes)
        }
        setBusy(false)
    }

    const current = status || 'unchecked'
    const canApply = valueMap && current === 'valid' && (companyName || companyAddress)

    return (
        <div>
            <Row>
                <FormGroup.Control type='text' id={inputId} name={name} value={value || ''}
                    onChange={onChange} placeholder={placeholder} autoComplete='off'
                    {...(required && { required: true })}
                    {...(inputRef && { ref: inputRef })}
                    data-testid={testId} />
                <IconButton icon={Icon_CheckMark} $variant='primary' $rounded $size='2.25rem' $marginRight='0'
                    disabled={!value || busy} clickAction={check}
                    title={t(busy ? 'all.vatChecker.checking' : 'all.vatChecker.check')}
                    ariaLabel={t('all.vatChecker.check')} testId={`${testId}.check`} />
                {canApply && (
                    <IconButton icon={Icon_Download} $variant='secondary' $rounded $size='2.25rem' $marginRight='0'
                        disabled={busy} clickAction={apply}
                        title={t('all.vatChecker.apply')} ariaLabel={t('all.vatChecker.apply')}
                        testId={`${testId}.apply`} />
                )}
            </Row>
            {value && (
                <Badge $tone={TONE[current] || 'disabled'} data-testid={`${testId}.status`}>
                    {t(busy ? 'all.vatChecker.checking' : `all.vatChecker.status.${current}`)}
                    {!busy && current !== 'unchecked' && checkedAt ? ` · ${ld(checkedAt)}` : ''}
                </Badge>
            )}
            {current === 'valid' && (companyName || companyAddress) && (
                <Detail>{[companyName, companyAddress].filter(Boolean).join(' · ')}</Detail>
            )}
            {problem && <Detail role='alert'>{t(`all.vatChecker.error.${problem}`)}</Detail>}
        </div>
    )
}

export default VatChecker
